import * as RadPopover from '@radix-ui/react-popover'
import React, { useState } from 'react'
import styled from 'styled-components'

import {
  ArrowDown2Icon,
  ArrowUp2Icon,
  Separator as Separator_,
} from '@root/shared/ui-kit'

import type { LimitOrder } from '@root/entities/limit-order'

type Props = {
  items: Array<{
    value: LimitOrder.TargetType
    label: React.ReactNode
  }>
  value: LimitOrder.TargetType
  onChange: (value: LimitOrder.TargetType) => void
}

export const TradingDropdown: React.FC<Props> = ({
  items,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = (item: LimitOrder.TargetType) => {
    onChange(item)
    setOpen(false)
  }

  const label = items.find((item) => item.value === value)?.label
  const Icon = open ? ArrowUp2Icon : ArrowDown2Icon

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger open={open} onClick={() => setOpen(!open)}>
        <Label>{label}</Label>
        <Icon size={12} />
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="center">
          <Item onClick={() => handleSelect(value)} $active>
            {label}
            <ArrowUp2Icon size={12} />
          </Item>
          <Separator />
          {items
            .filter((item) => item.value !== value)
            .map((item) => (
              <Item onClick={() => handleSelect(item.value)} key={item.value}>
                {item.label}
              </Item>
            ))}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)`
  position: relative;
`

const Trigger = styled(RadPopover.Trigger)<{
  open: boolean
}>`
  background: ${({ open }) => (open ? 'var(--backgroundA-2)' : 'transparent')};
  color: ${({ open }) => (open ? 'var(--text-12)' : 'var(--text-11)')};

  display: grid;
  overflow: hidden;
  align-items: center;

  border-radius: 8px;
  grid-template-columns: 1fr auto;
  padding-inline: 7px;
  border: 0;

  width: 78px;
  height: 100%;

  font-weight: 600;
  font-size: 11px;

  cursor: pointer;

  &:hover {
    background: var(--backgroundA-2);
    color: var(--text-12);
  }

  position: relative;
`

const Content = styled(RadPopover.Content)`
  background: var(--backgroundA-2);
  backdrop-filter: blur(35px);

  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  width: 78px;
  border-radius: 8px;

  cursor: pointer;
  overflow: hidden;

  margin-top: -33px;
`

const Item = styled.div<{
  $active?: boolean
}>`
  color: ${({ $active }) => ($active ? 'var(--text-12)' : 'var(--text-8)')};

  border: 0;
  background: none;
  transition: all 100ms ease-in;
  width: 100%;

  display: grid;
  align-items: center;
  text-align: left;
  overflow: hidden;
  padding: 0 7px;

  grid-template-columns: 26px 1fr;
  font-size: 11px;
  font-weight: 600;
  height: 32px;

  display: grid;
  grid-template-columns: 1fr auto;

  &:hover {
    color: var(--text-12);
  }
`

const Label = styled.span`
  gap: 3px;

  overflow: hidden;
  display: grid;

  text-align: left;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Separator = styled(Separator_)`
  background: var(--backgroundA-2);
  width: 48px;
  margin: auto;
`
